/**
 * ReminderRuleFormDialog - Dialog premium de criação/edição de regra de lembrete
 * 
 * Layout split-pane com formulário organizado em FormSectionCard
 * e preview multi-canal em tempo real (WhatsApp, SMS, Email, Telegram, etc.)
 * 
 * Tipo de mensagem é derivado automaticamente pelo canal da inbox:
 * - WhatsApp Oficial (WABA) → template (lista templates aprovados)
 * - Demais canais → texto livre com variáveis
 * 
 * @version 5.0.0
 * RELEASE: 2026-03-29 v1.0.0 - Criação do componente
 * RELEASE: 2026-03-29 v2.0.0 - Redesign premium com layout split-pane e preview
 * RELEASE: 2026-03-29 v3.0.0 - Auto-detectar tipo mensagem por canal, buscar templates WABA
 * RELEASE: 2026-03-29 v4.0.0 - Corrigir detecção WABA via channel_type + mapeamento variáveis posicionais
 * RELEASE: 2026-09-12 v5.0.0 - Escopo por agenda (scheduling): seleção múltipla, vazio = todas
 * RELEASE: 2026-10-02 v6.0.0 - Vistoria: bloco de configuração (status, referência, destinatários,
 *          repetição, Tipo de Checklist) e mapeamento do botão de URL dinâmica do template
 */

import { useEffect, useRef, useCallback, useState, useMemo } from 'react';
import { Switch } from '@/components/ui/switch';
import {
  InspectionReminderConfigFields,
  DEFAULT_INSPECTION_CONFIG,
  hasFinalInspectionStatus,
} from '@/components/reminders/InspectionReminderConfigFields';
import type { InspectionReminderConfig } from '@/hooks/useReminderRules';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import {
  Loader2,
  Settings,
  MessageSquare,
  Variable,
  Eye,
  Sparkles,
  ChevronDown,
  Gauge,
} from 'lucide-react';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';
import { useAuth } from '@/hooks/useAuth';
import { useInboxes, type InboxData } from '@/hooks/useInboxes';
import { useSchedulingEventTypes } from '@/hooks/useSchedulingEventTypes';
import { Checkbox } from '@/components/ui/checkbox';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { supabase } from '@/integrations/supabase/client';
import { createLogger } from '@/lib/logger';
import { FormSectionCard } from '@/components/crm/deals/components/FormSectionCard';
import { ReminderMessagePreview } from './preview/ReminderMessagePreview';
import {
  OFFSET_OPTIONS_BEFORE,
  OFFSET_OPTIONS_AFTER,
  TEMPLATE_VARIABLES,
  type ReminderRule,
  type ReminderEntityType,
  type UpsertReminderRuleInput,
} from '@/hooks/useReminderRules';
import type { Json } from '@/integrations/supabase/types';

const logger = createLogger('ReminderRuleFormDialog');

// ============================================================================
// TIPOS
// ============================================================================

/** Template WABA aprovado retornado da query */
interface WabaTemplate {
  id: string;
  name: string;
  language: string;
  components: Json;
  category: string;
}

// ============================================================================
// SCHEMA DE VALIDAÇÃO — message_type removido (derivado pelo canal)
// ============================================================================

const formSchema = z.object({
  inbox_id: z.string().min(1, 'Selecione uma inbox'),
  offset_minutes: z.number({ required_error: 'Selecione o momento do disparo' }),
  message_template: z.string().optional(),
  /** ID do template WABA selecionado (usado apenas para inboxes oficiais) */
  selected_template_id: z.string().optional(),
});

type FormValues = z.infer<typeof formSchema>;

// ============================================================================
// PROPS
// ============================================================================

interface ReminderRuleFormDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Tipo de entidade (scheduling, cobranca, etc.) */
  entityType: ReminderEntityType;
  /** Regra existente para edição (null = criação) */
  editingRule?: ReminderRule | null;
  /** Callback de submit */
  onSubmit: (input: UpsertReminderRuleInput) => void;
  /** Se está salvando */
  isSubmitting?: boolean;
  /** Permitir offsets positivos (após vencimento) — usado para cobranças */
  allowPositiveOffsets?: boolean;
  /** Regras já existentes — usado para desabilitar offsets em uso */
  existingRules?: ReminderRule[];
}

// ============================================================================
// MAPEAMENTO DE ÍCONE POR CANAL
// ============================================================================

/** Retorna emoji/label para o tipo de canal */
function getChannelLabel(channelType: string): string {
  const map: Record<string, string> = {
    whatsapp: '📱 WhatsApp',
    sms: '💬 SMS',
    email: '📧 Email',
    telegram: '✈️ Telegram',
    instagram: '📸 Instagram',
    webchat: '🌐 Webchat',
  };
  return map[channelType?.toLowerCase()] || channelType || 'Canal';
}

/**
 * Extrai o body text de um array de components WABA
 * Procura o componente com type === 'BODY' e retorna seu text
 */
function extractTemplateBody(components: Json): string {
  if (!Array.isArray(components)) return '';
  const body = components.find(
    (c: any) => c?.type === 'BODY' || c?.type === 'body'
  );
  return (body as any)?.text || '';
}

/**
 * Extrai variáveis posicionais {{1}}, {{2}}, etc. do body do template WABA
 * Retorna array ordenado: ['1', '2', '3', ...]
 */
function extractPositionalVariables(bodyText: string): string[] {
  const matches = bodyText.match(/\{\{(\d+)\}\}/g);
  if (!matches) return [];
  const numbers = [...new Set(matches.map(m => m.replace(/[{}]/g, '')))];
  return numbers.sort((a, b) => Number(a) - Number(b));
}

// ============================================================================
// COMPONENTE PRINCIPAL
// ============================================================================

export function ReminderRuleFormDialog({
  open,
  onOpenChange,
  entityType,
  editingRule,
  onSubmit,
  isSubmitting,
  allowPositiveOffsets = false,
  existingRules = [],
}: ReminderRuleFormDialogProps) {
  const { currentTenant } = useAuth();
  const { data: inboxes = [] } = useInboxes(currentTenant?.id);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  // Estado para mapeamento de variáveis posicionais do template WABA
  const [variableMapping, setVariableMapping] = useState<Record<string, string>>({});
  
  // Estado local para templates WABA buscados
  const [wabaTemplates, setWabaTemplates] = useState<WabaTemplate[]>([]);
  const [loadingTemplates, setLoadingTemplates] = useState(false);

  // Estados de throttling em SEGUNDOS (UI amigável — conversão ms↔s na leitura/gravação)
  const [delayMinSec, setDelayMinSec] = useState(1);
  const [delayMaxSec, setDelayMaxSec] = useState(3);
  const [blockSize, setBlockSize] = useState(5);
  const [blockDelayMinSec, setBlockDelayMinSec] = useState(0.1);
  const [blockDelayMaxSec, setBlockDelayMaxSec] = useState(0.4);
  const [maxPerMinute, setMaxPerMinute] = useState(20);
  const [throttleOpen, setThrottleOpen] = useState(false);

  // Estado para backfill — aplicar regra a registros existentes (apenas criação)
  const [applyToExisting, setApplyToExisting] = useState(false);

  // v5.0.0: escopo por agenda (somente entity_type = 'scheduling'); vazio = todas as agendas
  const isScheduling = entityType === 'scheduling';
  // v6.0.0: configuração exclusiva de vistoria
  const isInspection = entityType === 'vistoria';
  const [inspectionConfig, setInspectionConfig] = useState<InspectionReminderConfig>(DEFAULT_INSPECTION_CONFIG);
  const [checklistTypeId, setChecklistTypeId] = useState<string>('all');
  /** Variável ligada ao sufixo {{1}} do botão de URL dinâmica (índice → chave) */
  const [buttonMapping, setButtonMapping] = useState<Record<number, string>>({});
  const [scopeEntityIds, setScopeEntityIds] = useState<string[]>([]);
  const { eventTypes } = useSchedulingEventTypes();
  /** Agendas ativas disponíveis para escopo (mantém as já selecionadas mesmo se inativas) */
  const scopeOptions = useMemo(() => {
    if (!isScheduling) return [];
    return eventTypes.filter((et) => et.is_active || scopeEntityIds.includes(et.id));
  }, [isScheduling, eventTypes, scopeEntityIds]);

  /** Alterna uma agenda no escopo */
  const toggleScopeEntity = useCallback((id: string) => {
    setScopeEntityIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  }, []);

  // Filtrar apenas inboxes ativas
  const activeInboxes = (inboxes as InboxData[]).filter(
    (i) => !['inactive', 'suspended', 'error', 'disconnected'].includes(i.status)
  );

  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      inbox_id: '',
      offset_minutes: -60,
      message_template: '',
      selected_template_id: '',
    },
  });

  // Preencher formulário ao editar
  useEffect(() => {
    if (editingRule && open) {
      form.reset({
        inbox_id: editingRule.inbox_id,
        offset_minutes: editingRule.offset_minutes,
        message_template: editingRule.message_template || '',
        selected_template_id: '',
      });
      // Preencher valores de throttling convertendo ms → segundos
      setDelayMinSec((editingRule.delay_min_ms ?? 1000) / 1000);
      setDelayMaxSec((editingRule.delay_max_ms ?? 3000) / 1000);
      setBlockSize(editingRule.block_size ?? 5);
      setBlockDelayMinSec((editingRule.block_delay_min_ms ?? 100) / 1000);
      setBlockDelayMaxSec((editingRule.block_delay_max_ms ?? 400) / 1000);
      setMaxPerMinute(editingRule.max_per_minute ?? 20);
      // v5.0.0: escopo por agenda da regra em edição (null => todas)
      setScopeEntityIds(editingRule.scope_entity_ids ?? []);
      // v6.0.0: restaura configuração de vistoria
      if (isInspection) {
        const cfg = editingRule.config as InspectionReminderConfig | null | undefined;
        setInspectionConfig(cfg && Array.isArray(cfg.statuses) ? cfg : DEFAULT_INSPECTION_CONFIG);
        setChecklistTypeId(editingRule.scope_entity_ids?.[0] ?? 'all');
        const bp = (editingRule.template_params as { button_params?: Array<{ index: number; value: string }> } | null)?.button_params;
        const restoredBtn: Record<number, string> = {};
        (bp || []).forEach((b) => { restoredBtn[b.index] = b.value; });
        setButtonMapping(restoredBtn);
      }
    } else if (!editingRule && open) {
      form.reset({
        inbox_id: '',
        offset_minutes: -60,
        message_template: '',
        selected_template_id: '',
      });
      setWabaTemplates([]);
      // Resetar throttling para defaults (em segundos)
      setDelayMinSec(1);
      setDelayMaxSec(3);
      setBlockSize(5);
      setBlockDelayMinSec(0.1);
      setBlockDelayMaxSec(0.4);
      setMaxPerMinute(20);
      setApplyToExisting(false);
      setScopeEntityIds([]);
      // v6.0.0: valores padrão da vistoria em nova regra
      setInspectionConfig(DEFAULT_INSPECTION_CONFIG);
      setChecklistTypeId('all');
      setButtonMapping({});
    }
  }, [editingRule, open, form]);

  // Valores observados para o preview em tempo real
  const messageTemplate = form.watch('message_template');
  const selectedInboxId = form.watch('inbox_id');
  const selectedTemplateId = form.watch('selected_template_id');

  /**
   * Calcula offsets já em uso para a inbox selecionada.
   * Distingue regras ativas ("Em uso") de inativas ("Inativa") para UX informativa.
   * Na edição, exclui a regra sendo editada para permitir manter o offset atual.
   */
  const { usedOffsets, inactiveOffsets } = useMemo(() => {
    // v6.0.0: vistoria permite vários lembretes no mesmo momento (status diferentes)
    if (isInspection || !existingRules || existingRules.length === 0) {
      return { usedOffsets: new Set<number>(), inactiveOffsets: new Set<number>() };
    }
    /** Assinatura do escopo para comparação (ordenada) — v5.0.0 */
    const scopeKey = (ids: string[] | null | undefined) => [...(ids ?? [])].sort().join(',');
    const currentScopeKey = scopeKey(scopeEntityIds);

    const relevantRules = existingRules.filter(
      r =>
        r.inbox_id === selectedInboxId &&
        r.id !== editingRule?.id &&
        (!isScheduling || scopeKey(r.scope_entity_ids) === currentScopeKey)
    );
    return {
      /** Offsets de regras ATIVAS — bloqueados para seleção */
      usedOffsets: new Set(relevantRules.filter(r => r.is_active).map(r => r.offset_minutes)),
      /** Offsets de regras INATIVAS — bloqueados mas com badge diferenciado */
      inactiveOffsets: new Set(relevantRules.filter(r => !r.is_active).map(r => r.offset_minutes)),
    };
  }, [existingRules, selectedInboxId, editingRule?.id, isScheduling, isInspection, scopeEntityIds]);

  // Dados da inbox selecionada
  const selectedInbox = activeInboxes.find((i) => i.id === selectedInboxId);
  // v4.0.0: Detecção correta via channel_type (padrão do sistema inteiro)
  const isWhatsAppOfficial = selectedInbox?.channel_type === 'whatsapp_oficial';

  // Tipo de mensagem derivado automaticamente pelo canal
  const derivedMessageType = useMemo(() => {
    return isWhatsAppOfficial ? 'template' : 'text';
  }, [isWhatsAppOfficial]);

  // Template WABA selecionado (para preview)
  const selectedWabaTemplate = useMemo(() => {
    if (!selectedTemplateId) return null;
    return wabaTemplates.find((t) => t.id === selectedTemplateId) || null;
  }, [selectedTemplateId, wabaTemplates]);

  // v6.0.0: botões de URL dinâmica do template (URL terminando em {{1}}), com índice Meta
  const dynamicUrlButtons = useMemo(() => {
    const comps = selectedWabaTemplate?.components;
    if (!Array.isArray(comps)) return [] as Array<{ index: number; text: string; url: string }>;
    const btnComp = comps.find((c: any) => String(c?.type).toUpperCase() === 'BUTTONS') as any;
    const buttons: any[] = Array.isArray(btnComp?.buttons) ? btnComp.buttons : [];
    return buttons
      .map((b, index) => ({ index, text: String(b?.text ?? ''), url: String(b?.url ?? ''), type: String(b?.type ?? '').toUpperCase() }))
      .filter((b) => b.type === 'URL' && /\{\{1\}\}\s*$/.test(b.url));
  }, [selectedWabaTemplate]);

  // v4.0.0: Variáveis posicionais extraídas do body do template WABA selecionado
  const positionalVars = useMemo(() => {
    if (!selectedWabaTemplate) return [];
    const body = extractTemplateBody(selectedWabaTemplate.components);
    return extractPositionalVariables(body);
  }, [selectedWabaTemplate]);

  // Resetar mapeamento quando template muda
  useEffect(() => {
    if (positionalVars.length > 0) {
      setVariableMapping((prev) => {
        const next: Record<string, string> = {};
        positionalVars.forEach((v) => {
          next[v] = prev[v] || ''; // Preserva valores já mapeados
        });
        return next;
      });
    } else {
      setVariableMapping({});
    }
  }, [positionalVars]);

  // Texto para o preview (texto livre ou body do template WABA)
  const previewText = useMemo(() => {
    if (derivedMessageType === 'template' && selectedWabaTemplate) {
      return extractTemplateBody(selectedWabaTemplate.components);
    }
    return messageTemplate || '';
  }, [derivedMessageType, selectedWabaTemplate, messageTemplate]);

  // Buscar templates WABA quando inbox oficial for selecionada
  useEffect(() => {
    if (!isWhatsAppOfficial || !selectedInboxId) {
      setWabaTemplates([]);
      return;
    }

    const fetchTemplates = async () => {
      setLoadingTemplates(true);
      logger.debug('Buscando templates WABA para inbox', { inboxId: selectedInboxId });

      const { data, error } = await supabase
        .from('whatsapp_templates')
        .select('id, name, language, components, category')
        .eq('inbox_id', selectedInboxId)
        .eq('status', 'APPROVED')
        .order('name');

      if (error) {
        logger.error('Erro ao buscar templates WABA', { error: error.message });
        setWabaTemplates([]);
      } else {
        setWabaTemplates((data || []) as WabaTemplate[]);
        logger.debug('Templates WABA carregados', { count: data?.length || 0 });
      }
      setLoadingTemplates(false);
    };

    fetchTemplates();
  }, [selectedInboxId, isWhatsAppOfficial]);

  // Quando editando regra tipo template, pré-selecionar o template pelo nome
  useEffect(() => {
    if (
      editingRule &&
      open &&
      isWhatsAppOfficial &&
      editingRule.template_name &&
      wabaTemplates.length > 0
    ) {
      const match = wabaTemplates.find(
        (t) =>
          t.name === editingRule.template_name &&
          t.language === (editingRule.template_language || 'pt_BR')
      );
      if (match) {
        form.setValue('selected_template_id', match.id);
      }
    }

    // v4.0.0: Pré-carregar mapeamento de variáveis posicionais ao editar regra com template_params
    if (editingRule?.template_params && typeof editingRule.template_params === 'object') {
      const params = editingRule.template_params as { body_params?: string[] };
      if (params.body_params && Array.isArray(params.body_params)) {
        const restored: Record<string, string> = {};
        params.body_params.forEach((val: string, idx: number) => {
          restored[String(idx + 1)] = val || '';
        });
        setVariableMapping(restored);
      }
    }
  }, [editingRule, open, isWhatsAppOfficial, wabaTemplates, form]);

  // Variáveis de template disponíveis para o entity_type
  const variables = TEMPLATE_VARIABLES[entityType] || [];

  /** Inserir variável na posição do cursor do textarea */
  const insertVariable = useCallback(
    (key: string) => {
      const textarea = textareaRef.current;
      if (!textarea) {
        // Fallback: adiciona ao final
        const current = form.getValues('message_template') || '';
        form.setValue('message_template', current + key, { shouldDirty: true });
        toast.success(`Variável ${key} inserida!`);
        return;
      }

      const start = textarea.selectionStart;
      const end = textarea.selectionEnd;
      const current = form.getValues('message_template') || '';
      const newValue = current.substring(0, start) + key + current.substring(end);
      form.setValue('message_template', newValue, { shouldDirty: true });

      // Reposiciona o cursor após a variável inserida
      requestAnimationFrame(() => {
        textarea.focus();
        const newPos = start + key.length;
        textarea.setSelectionRange(newPos, newPos);
      });

      toast.success(`Variável ${key} inserida!`);
    },
    [form]
  );

  /** Handler de submit — inclui parâmetros de throttling (conversão s→ms) */
  const handleSubmit = (values: FormValues) => {
    // Validação: mín ≤ máx para pares de delay
    if (delayMinSec > delayMaxSec) {
      toast.error('Delay entre mensagens: valor mínimo não pode ser maior que o máximo.');
      return;
    }
    if (blockDelayMinSec > blockDelayMaxSec) {
      toast.error('Delay entre blocos: valor mínimo não pode ser maior que o máximo.');
      return;
    }

    // Converter segundos → milissegundos para gravação no banco
    const throttleParams = {
      delay_min_ms: Math.round(delayMinSec * 1000),
      delay_max_ms: Math.round(delayMaxSec * 1000),
      block_size: blockSize,
      block_delay_min_ms: Math.round(blockDelayMinSec * 1000),
      block_delay_max_ms: Math.round(blockDelayMaxSec * 1000),
      max_per_minute: maxPerMinute,
    };

    // v6.0.0: validações e campos extras de vistoria
    const inspectionExtras = isInspection
      ? {
          config: inspectionConfig,
          scope_entity_ids: checklistTypeId !== 'all' ? [checklistTypeId] : null,
        }
      : {};
    if (isInspection) {
      if (inspectionConfig.statuses.length === 0) {
        toast.error('Selecione ao menos um status da vistoria.');
        return;
      }
      if (inspectionConfig.recipient_modes.length === 0) {
        toast.error('Selecione ao menos um tipo de responsável.');
        return;
      }
      // Status finalizados só contam a partir da entrada no status (validação repetida no banco)
      if (hasFinalInspectionStatus(inspectionConfig.statuses) && inspectionConfig.anchor !== 'status_entered') {
        toast.error('Status finalizados só podem contar a partir da entrada no status.');
        return;
      }
    }

    // Se canal oficial WABA, busca dados do template selecionado
    if (derivedMessageType === 'template') {
      if (!selectedWabaTemplate) {
        toast.error('Selecione um template aprovado.');
        return;
      }

      // v4.0.0: Montar body_params com o mapeamento de variáveis posicionais
      const bodyParams = positionalVars.map((v) => variableMapping[v] || '');
      
      // Validar que todas as variáveis posicionais foram mapeadas
      const unmapped = positionalVars.filter((v) => !variableMapping[v]);
      if (unmapped.length > 0) {
        toast.error(`Mapeie todas as variáveis do template: {{${unmapped.join('}}, {{')}}}}`);
        return;
      }
      if (isInspection && dynamicUrlButtons.some((b) => !buttonMapping[b.index])) {
        toast.error('Escolha a variável do botão de link do template.');
        return;
      }

      onSubmit({
        rule_id: editingRule?.id,
        entity_type: entityType,
        inbox_id: values.inbox_id,
        offset_minutes: values.offset_minutes,
        message_type: 'template',
        template_name: selectedWabaTemplate.name,
        template_language: selectedWabaTemplate.language,
        template_params: {
          body_params: bodyParams,
          // v6.0.0: sufixo do botão de URL dinâmica (vistoria)
          ...(isInspection && dynamicUrlButtons.length > 0
            ? { button_params: dynamicUrlButtons.map((b) => ({ index: b.index, value: buttonMapping[b.index] || '' })) }
            : {}),
        } as unknown as Json,
        is_active: editingRule?.is_active ?? true,
        ...throttleParams,
        // Backfill: apenas na criação
        ...(!editingRule && applyToExisting ? { apply_to_existing: true } : {}),
        // v5.0.0: escopo por agenda (vazio = todas as agendas)
        scope_entity_ids: isScheduling ? scopeEntityIds : null,
        ...inspectionExtras,
      });
    } else {
      // Texto livre para canais não-oficiais
      onSubmit({
        rule_id: editingRule?.id,
        entity_type: entityType,
        inbox_id: values.inbox_id,
        offset_minutes: values.offset_minutes,
        message_type: 'text',
        message_template: values.message_template,
        is_active: editingRule?.is_active ?? true,
        ...throttleParams,
        // Backfill: apenas na criação
        ...(!editingRule && applyToExisting ? { apply_to_existing: true } : {}),
        // v5.0.0: escopo por agenda (vazio = todas as agendas)
        scope_entity_ids: isScheduling ? scopeEntityIds : null,
        ...inspectionExtras,
      });
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="sm:max-w-4xl p-0 gap-0 overflow-hidden max-h-[90vh]"
        hideCloseButton={false}
      >
        {/* ===== HEADER PREMIUM ===== */}
        <div className="px-6 py-4 border-b bg-gradient-to-r from-primary/5 via-primary/3 to-transparent">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-base">
              <div className="flex h-7 w-7 items-center justify-center rounded-lg bg-primary/10">
                <Sparkles className="h-3.5 w-3.5 text-primary" />
              </div>
              {editingRule ? 'Editar regra de lembrete' : 'Nova regra de lembrete'}
            </DialogTitle>
            <DialogDescription className="text-xs text-muted-foreground">
              Configure o canal, momento do disparo e a mensagem do lembrete automático.
            </DialogDescription>
          </DialogHeader>
        </div>

        {/* ===== CORPO SPLIT-PANE ===== */}
        <div className="grid grid-cols-1 lg:grid-cols-[1fr,340px] min-h-0 flex-1">
          {/* ===== PAINEL ESQUERDO — FORMULÁRIO ===== */}
          <ScrollArea className="max-h-[calc(90vh-140px)]">
            <div className="p-5 space-y-4">
              <Form {...form}>
                <form
                  id="reminder-rule-form"
                  onSubmit={form.handleSubmit(handleSubmit)}
                  className="space-y-4"
                >
                  {/* --- SEÇÃO: Canal & Disparo --- */}
                  <FormSectionCard title="Canal & Disparo" icon={Settings}>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      {/* Seletor de Inbox */}
                      <FormField
                        control={form.control}
                        name="inbox_id"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel className="text-xs">Canal de envio</FormLabel>
                            <Select onValueChange={field.onChange} value={field.value}>
                              <FormControl>
                                <SelectTrigger className="h-9 text-xs">
                                  <SelectValue placeholder="Selecione a inbox" />
                                </SelectTrigger>
                              </FormControl>
                              <SelectContent>
                                {activeInboxes.map((inbox) => (
                                  <SelectItem key={inbox.id} value={inbox.id}>
                                    <span className="flex items-center gap-2 text-xs">
                                      <Badge
                                        variant="outline"
                                        className="text-[8px] px-1 py-0 h-4"
                                      >
                                        {inbox.channel_type}
                                      </Badge>
                                      <span className="truncate">{inbox.name}</span>
                                    </span>
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                            <FormMessage />
                          </FormItem>
                        )}
                      />

                      {/* Seletor de Offset */}
                      <FormField
                        control={form.control}
                        name="offset_minutes"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel className="text-xs">Momento do disparo</FormLabel>
                            <Select
                              onValueChange={(v) => field.onChange(Number(v))}
                              value={String(field.value)}
                            >
                              <FormControl>
                                <SelectTrigger className="h-9 text-xs">
                                  <SelectValue placeholder="Quando enviar?" />
                                </SelectTrigger>
                              </FormControl>
                              <SelectContent>
                                {/* Grupo: Antes */}
                                <div className="px-2 py-1.5 text-[10px] uppercase tracking-wider text-muted-foreground font-semibold">
                                  Antes
                                </div>
                                {OFFSET_OPTIONS_BEFORE.map((opt) => {
                                  const isUsed = usedOffsets.has(opt.value);
                                  const isInactive = inactiveOffsets.has(opt.value);
                                  return (
                                    <SelectItem
                                      key={opt.value}
                                      value={String(opt.value)}
                                      disabled={isUsed || isInactive}
                                    >
                                      <span className="flex items-center gap-2">
                                        {opt.label}
                                        {isUsed && (
                                          <Badge variant="secondary" className="text-[8px] px-1 py-0 h-4 ml-auto">
                                            Em uso
                                          </Badge>
                                        )}
                                        {isInactive && !isUsed && (
                                          <Badge variant="outline" className="text-[8px] px-1 py-0 h-4 ml-auto text-muted-foreground">
                                            Inativa — reative
                                          </Badge>
                                        )}
                                      </span>
                                    </SelectItem>
                                  );
                                })}

                                {/* Grupo: Depois (só para cobranças) */}
                                {allowPositiveOffsets && (
                                  <>
                                    <div className="px-2 py-1.5 text-[10px] uppercase tracking-wider text-muted-foreground font-semibold border-t mt-1 pt-2">
                                      {isInspection ? 'Depois' : 'Após vencimento'}
                                    </div>
                                    {OFFSET_OPTIONS_AFTER.map((opt) => {
                                      const isUsed = usedOffsets.has(opt.value);
                                      const isInactive = inactiveOffsets.has(opt.value);
                                      return (
                                        <SelectItem
                                          key={opt.value}
                                          value={String(opt.value)}
                                          disabled={isUsed || isInactive}
                                        >
                                          <span className="flex items-center gap-2">
                                            {opt.label}
                                            {isUsed && (
                                              <Badge variant="secondary" className="text-[8px] px-1 py-0 h-4 ml-auto">
                                                Em uso
                                              </Badge>
                                            )}
                                            {isInactive && !isUsed && (
                                              <Badge variant="outline" className="text-[8px] px-1 py-0 h-4 ml-auto text-muted-foreground">
                                                Inativa — reative
                                              </Badge>
                                            )}
                                          </span>
                                        </SelectItem>
                                      );
                                    })}
                                  </>
                                )}
                              </SelectContent>
                            </Select>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    </div>

                    {/* v5.0.0: Escopo por agenda — somente lembretes de agendamento */}
                    {isScheduling && (
                      <div className="space-y-1.5">
                        <Label className="text-xs">Agendas</Label>
                        <Popover>
                          <PopoverTrigger asChild>
                            <Button
                              type="button"
                              variant="outline"
                              className="w-full h-9 justify-between text-xs font-normal"
                            >
                              <span className="truncate">
                                {scopeEntityIds.length === 0
                                  ? 'Todas as agendas'
                                  : `${scopeEntityIds.length} agenda(s) selecionada(s)`}
                              </span>
                              <ChevronDown className="h-3.5 w-3.5 opacity-60" />
                            </Button>
                          </PopoverTrigger>
                          <PopoverContent align="start" className="w-[320px] p-2">
                            <ScrollArea className="max-h-56">
                              <div className="space-y-1 pr-2">
                                {scopeOptions.length === 0 && (
                                  <p className="text-xs text-muted-foreground p-2">
                                    Nenhuma agenda cadastrada.
                                  </p>
                                )}
                                {scopeOptions.map((et) => (
                                  <label
                                    key={et.id}
                                    className="flex items-center gap-2 rounded-md px-2 py-1.5 text-xs hover:bg-muted cursor-pointer"
                                  >
                                    <Checkbox
                                      checked={scopeEntityIds.includes(et.id)}
                                      onCheckedChange={() => toggleScopeEntity(et.id)}
                                    />
                                    <span className="truncate">{et.title}</span>
                                    {!et.is_active && (
                                      <Badge variant="outline" className="ml-auto text-[9px] h-4 px-1">
                                        Inativa
                                      </Badge>
                                    )}
                                  </label>
                                ))}
                              </div>
                            </ScrollArea>
                          </PopoverContent>
                        </Popover>

                        {/* Etiquetas do escopo selecionado */}
                        {scopeEntityIds.length > 0 && (
                          <div className="flex flex-wrap gap-1 pt-0.5">
                            {scopeOptions
                              .filter((et) => scopeEntityIds.includes(et.id))
                              .map((et) => (
                                <Badge key={et.id} variant="secondary" className="text-[10px] h-5">
                                  {et.title}
                                </Badge>
                              ))}
                          </div>
                        )}

                        <p className="text-[11px] text-muted-foreground">
                          Deixe vazio para valer para todas as agendas. Selecionando agendas,
                          o lembrete só é enviado para os agendamentos dessas agendas.
                        </p>
                      </div>
                    )}

                    {/* v6.0.0: configuração exclusiva de vistoria */}
                    {isInspection && (
                      <InspectionReminderConfigFields
                        value={inspectionConfig}
                        onChange={setInspectionConfig}
                        checklistTypeId={checklistTypeId}
                        onChecklistTypeChange={setChecklistTypeId}
                      />
                    )}

                    {/* v6.0.0: botão de URL dinâmica do template (regra Meta: só o final da URL) */}
                    {isInspection && isWhatsAppOfficial && dynamicUrlButtons.map((b) => (
                      <div key={b.index} className="space-y-1.5 rounded-lg border p-3">
                        <p className="text-xs font-medium">Botão "{b.text}" — final do link</p>
                        <p className="text-[11px] text-muted-foreground break-all">
                          URL cadastrada: {b.url}. Use "Final do link" para abrir a vistoria (cadastre na Meta como https://SEU-DOMINIO/v/{'{{1}}'}).
                        </p>
                        <Select
                          value={buttonMapping[b.index] || ''}
                          onValueChange={(v) => setButtonMapping((prev) => ({ ...prev, [b.index]: v }))}
                        >
                          <SelectTrigger className="h-8 text-xs">
                            <SelectValue placeholder="Escolha a variável" />
                          </SelectTrigger>
                          <SelectContent>
                            {variables.map((v) => (
                              <SelectItem key={v.key} value={v.key}>{v.label}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    ))}

                    {/* Indicador do canal selecionado */}
                    {selectedInbox && (
                      <div className="flex items-center gap-2 px-2.5 py-1.5 bg-primary/5 border border-primary/10 rounded-lg">
                        <span className="text-xs text-muted-foreground">Canal:</span>
                        <Badge variant="secondary" className="text-[10px] h-5">
                          {getChannelLabel(selectedInbox.channel_type)}
                        </Badge>
                        {isWhatsAppOfficial && (
                          <Badge variant="outline" className="text-[10px] h-5 text-green-600 border-green-200">
                            Oficial
                          </Badge>
                        )}
                        {/* Indicador do tipo de mensagem detectado */}
                        <Badge
                          variant="outline"
                          className={cn(
                            'text-[10px] h-5 ml-auto',
                            derivedMessageType === 'template'
                              ? 'text-amber-600 border-amber-200'
                              : 'text-blue-600 border-blue-200'
                          )}
                        >
                          {derivedMessageType === 'template' ? '📋 Template' : '✏️ Texto livre'}
                        </Badge>
                      </div>
                    )}
                  </FormSectionCard>

                  {/* --- SEÇÃO: Mensagem --- */}
                  <FormSectionCard title="Mensagem" icon={MessageSquare}>
                    {/* MODO TEXTO LIVRE — canais não-oficiais */}
                    {derivedMessageType === 'text' && (
                      <FormField
                        control={form.control}
                        name="message_template"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel className="text-xs">Conteúdo da mensagem</FormLabel>
                            <FormControl>
                              <Textarea
                                {...field}
                                ref={(el) => {
                                  // Combina ref do react-hook-form com nossa ref local
                                  (textareaRef as React.MutableRefObject<HTMLTextAreaElement | null>).current = el;
                                  if (typeof field.ref === 'function') field.ref(el);
                                }}
                                placeholder="Olá {{nome_completo}}, este é um lembrete sobre..."
                                rows={5}
                                className="resize-none text-sm"
                              />
                            </FormControl>
                            <p className="text-[10px] text-muted-foreground mt-1">
                              Clique nas variáveis abaixo para inserir na mensagem.
                            </p>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    )}

                    {/* MODO TEMPLATE WABA — canais oficiais */}
                    {derivedMessageType === 'template' && (
                      <FormField
                        control={form.control}
                        name="selected_template_id"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel className="text-xs">Template aprovado</FormLabel>
                            {loadingTemplates ? (
                              <div className="flex items-center gap-2 py-3 text-xs text-muted-foreground">
                                <Loader2 className="w-3.5 h-3.5 animate-spin" />
                                Carregando templates...
                              </div>
                            ) : wabaTemplates.length === 0 ? (
                              <div className="py-3 px-3 bg-amber-50 dark:bg-amber-950/20 border border-amber-200 dark:border-amber-800 rounded-lg">
                                <p className="text-xs text-amber-700 dark:text-amber-400">
                                  ⚠️ Nenhum template aprovado encontrado para esta inbox.
                                </p>
                                <p className="text-[10px] text-amber-600 dark:text-amber-500 mt-1">
                                  Crie e submeta templates no menu Templates antes de usar aqui.
                                </p>
                              </div>
                            ) : (
                              <>
                                <Select onValueChange={field.onChange} value={field.value || ''}>
                                  <FormControl>
                                    <SelectTrigger className="h-9 text-xs">
                                      <SelectValue placeholder="Selecione um template aprovado" />
                                    </SelectTrigger>
                                  </FormControl>
                                  <SelectContent>
                                    {wabaTemplates.map((tpl) => (
                                      <SelectItem key={tpl.id} value={tpl.id}>
                                        <span className="flex items-center gap-2 text-xs">
                                          <Badge
                                            variant="outline"
                                            className="text-[8px] px-1 py-0 h-4"
                                          >
                                            {tpl.language}
                                          </Badge>
                                          <span className="truncate font-mono">{tpl.name}</span>
                                          <Badge
                                            variant="secondary"
                                            className="text-[8px] px-1 py-0 h-4 ml-auto"
                                          >
                                            {tpl.category}
                                          </Badge>
                                        </span>
                                      </SelectItem>
                                    ))}
                                  </SelectContent>
                                </Select>
                                {/* Preview do body do template selecionado */}
                                {selectedWabaTemplate && (
                                  <div className="mt-2 p-2.5 bg-muted/50 border rounded-lg">
                                    <p className="text-[10px] text-muted-foreground font-semibold mb-1 uppercase tracking-wider">
                                      Body do template
                                    </p>
                                    <p className="text-xs text-foreground whitespace-pre-wrap leading-relaxed">
                                      {extractTemplateBody(selectedWabaTemplate.components) || (
                                        <span className="text-muted-foreground italic">
                                          Sem conteúdo de body
                                        </span>
                                      )}
                                    </p>
                                  </div>
                                )}

                                {/* v4.0.0: Mapeamento de variáveis posicionais do template WABA */}
                                {selectedWabaTemplate && positionalVars.length > 0 && (
                                  <div className="mt-3 p-3 bg-primary/5 border border-primary/10 rounded-lg space-y-3">
                                    <div className="flex items-center gap-2">
                                      <Variable className="w-3.5 h-3.5 text-primary" />
                                      <p className="text-[10px] text-muted-foreground font-semibold uppercase tracking-wider">
                                        Mapeamento de variáveis
                                      </p>
                                    </div>
                                    <p className="text-[10px] text-muted-foreground">
                                      Associe cada variável posicional do template a uma variável do sistema de lembretes.
                                    </p>
                                    <div className="space-y-2">
                                      {positionalVars.map((varNum) => (
                                        <div key={varNum} className="grid grid-cols-[80px,1fr] gap-2 items-center">
                                          <Badge variant="outline" className="text-[10px] h-6 justify-center font-mono">
                                            {`{{${varNum}}}`}
                                          </Badge>
                                          <Select
                                            value={variableMapping[varNum] || ''}
                                            onValueChange={(val) =>
                                              setVariableMapping((prev) => ({ ...prev, [varNum]: val }))
                                            }
                                          >
                                            <SelectTrigger className="h-8 text-xs">
                                              <SelectValue placeholder="Selecione a variável..." />
                                            </SelectTrigger>
                                            <SelectContent>
                                              {variables.map((v) => (
                                                <SelectItem key={v.key} value={v.key}>
                                                  <span className="flex items-center gap-2 text-xs">
                                                    <span className="font-mono text-primary">{v.key}</span>
                                                    <span className="text-muted-foreground">— {v.label}</span>
                                                  </span>
                                                </SelectItem>
                                              ))}
                                            </SelectContent>
                                          </Select>
                                        </div>
                                      ))}
                                    </div>
                                  </div>
                                )}
                              </>
                            )}
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    )}
                  </FormSectionCard>

                  {/* --- SEÇÃO: Variáveis disponíveis (apenas texto livre) --- */}
                  {variables.length > 0 && derivedMessageType === 'text' && (
                    <FormSectionCard title="Variáveis disponíveis" icon={Variable} compact>
                      <p className="text-[10px] text-muted-foreground -mt-1 mb-2">
                        Clique em uma variável para inseri-la na posição do cursor.
                      </p>
                      <div className="flex flex-wrap gap-1.5">
                        {variables.map((v) => (
                          <Badge
                            key={v.key}
                            variant="secondary"
                            className={cn(
                              'text-[10px] cursor-pointer gap-1 transition-all duration-200',
                              'hover:bg-primary/15 hover:text-primary hover:shadow-sm hover:scale-[1.03]',
                              'active:scale-95'
                            )}
                            onClick={() => insertVariable(v.key)}
                          >
                            <span className="font-mono">{v.key}</span>
                            <span className="text-muted-foreground font-normal">— {v.label}</span>
                          </Badge>
                        ))}
                      </div>
                    </FormSectionCard>
                  )}

                  {/* --- SEÇÃO: Controle de Disparo (colapsável) --- */}
                  <Collapsible open={throttleOpen} onOpenChange={setThrottleOpen}>
                    <FormSectionCard compact>
                      <CollapsibleTrigger className="flex items-center justify-between w-full group">
                        <div className="flex items-center gap-2">
                          <div className="flex items-center justify-center w-6 h-6 rounded-md bg-primary/10">
                            <Gauge className="w-3.5 h-3.5 text-primary" />
                          </div>
                          <span className="text-[11px] uppercase tracking-wider text-muted-foreground font-semibold">
                            Controle de Disparo
                          </span>
                        </div>
                        <ChevronDown className={cn(
                          "w-4 h-4 text-muted-foreground transition-transform duration-200",
                          throttleOpen && "rotate-180"
                        )} />
                      </CollapsibleTrigger>

                      <CollapsibleContent className="mt-3 space-y-4">
                        <p className="text-[10px] text-muted-foreground">
                          Ajuste o ritmo de envio para evitar bloqueios e simular comportamento humano.
                        </p>

                        {/* Delay entre mensagens (segundos) */}
                        <div className="space-y-2">
                          <Label className="text-xs font-medium">Delay entre mensagens (segundos)</Label>
                          <div className="grid grid-cols-2 gap-2">
                            <div>
                              <span className="text-[10px] text-muted-foreground">Mínimo</span>
                              <Input
                                type="number"
                                min={0.1}
                                max={60}
                                step={0.1}
                                value={delayMinSec}
                                onChange={(e) => setDelayMinSec(Math.max(0.1, parseFloat(e.target.value) || 0.1))}
                                className="h-8 text-xs"
                              />
                            </div>
                            <div>
                              <span className="text-[10px] text-muted-foreground">Máximo</span>
                              <Input
                                type="number"
                                min={0.1}
                                max={60}
                                step={0.1}
                                value={delayMaxSec}
                                onChange={(e) => setDelayMaxSec(Math.max(0.1, parseFloat(e.target.value) || 0.1))}
                                className="h-8 text-xs"
                              />
                            </div>
                          </div>
                        </div>

                        {/* Tamanho do bloco */}
                        <div className="space-y-2">
                          <Label className="text-xs font-medium">Tamanho do bloco</Label>
                          <p className="text-[10px] text-muted-foreground -mt-1">
                            Quantidade de mensagens enviadas antes de uma pausa maior.
                          </p>
                          <Input
                            type="number"
                            min={1}
                            max={100}
                            value={blockSize}
                            onChange={(e) => setBlockSize(Math.max(1, parseInt(e.target.value) || 1))}
                            className="h-8 text-xs w-24"
                          />
                        </div>

                        {/* Delay entre blocos (segundos) */}
                        <div className="space-y-2">
                          <Label className="text-xs font-medium">Delay entre blocos (segundos)</Label>
                          <div className="grid grid-cols-2 gap-2">
                            <div>
                              <span className="text-[10px] text-muted-foreground">Mínimo</span>
                              <Input
                                type="number"
                                min={0.1}
                                max={300}
                                step={0.1}
                                value={blockDelayMinSec}
                                onChange={(e) => setBlockDelayMinSec(Math.max(0.1, parseFloat(e.target.value) || 0.1))}
                                className="h-8 text-xs"
                              />
                            </div>
                            <div>
                              <span className="text-[10px] text-muted-foreground">Máximo</span>
                              <Input
                                type="number"
                                min={0.1}
                                max={300}
                                step={0.1}
                                value={blockDelayMaxSec}
                                onChange={(e) => setBlockDelayMaxSec(Math.max(0.1, parseFloat(e.target.value) || 0.1))}
                                className="h-8 text-xs"
                              />
                            </div>
                          </div>
                        </div>

                        {/* Máximo por minuto */}
                        <div className="space-y-2">
                          <Label className="text-xs font-medium">Máximo por minuto</Label>
                          <p className="text-[10px] text-muted-foreground -mt-1">
                            Limite de mensagens enviadas por minuto (anti-ban).
                          </p>
                          <Input
                            type="number"
                            min={1}
                            max={120}
                            value={maxPerMinute}
                            onChange={(e) => setMaxPerMinute(Math.max(1, parseInt(e.target.value) || 1))}
                            className="h-8 text-xs w-24"
                          />
                        </div>

                        {/* Dica informativa */}
                        <div className="p-2.5 bg-primary/5 border border-primary/10 rounded-lg">
                          <p className="text-[10px] text-muted-foreground">
                            💡 Valores padrão são seguros para a maioria dos cenários. Ajuste apenas se necessário para alto volume de disparos.
                          </p>
                        </div>
                      </CollapsibleContent>
                    </FormSectionCard>
                  </Collapsible>

                  {/* --- SEÇÃO: Aplicar a registros existentes (apenas criação) --- */}
                  {!editingRule && (
                    <FormSectionCard title="Registros existentes" icon={Sparkles}>
                      <div className="flex items-center justify-between gap-3">
                        <div className="flex-1">
                          <p className="text-xs font-medium text-foreground">
                            Aplicar a registros existentes
                          </p>
                          <TooltipProvider>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <p className="text-[10px] text-muted-foreground cursor-help underline decoration-dotted">
                                  {entityType === 'scheduling'
                                    ? 'Gera lembretes para agendamentos confirmados e futuros já cadastrados.'
                                    : entityType === 'cobranca'
                                    ? 'Gera lembretes para cobranças pendentes ou vencidas já cadastradas.'
                                    : 'Gera lembretes para registros ativos já cadastrados.'}
                                </p>
                              </TooltipTrigger>
                              <TooltipContent side="bottom" className="max-w-xs text-xs">
                                O processamento acontece em segundo plano e não afeta a performance do sistema.
                                Registros cujo momento de disparo já passou serão ignorados automaticamente.
                              </TooltipContent>
                            </Tooltip>
                          </TooltipProvider>
                        </div>
                        <Switch
                          checked={applyToExisting}
                          onCheckedChange={setApplyToExisting}
                        />
                      </div>
                    </FormSectionCard>
                  )}
                </form>
              </Form>
            </div>
          </ScrollArea>

          {/* ===== PAINEL DIREITO — PREVIEW ===== */}
          <aside className="hidden lg:flex flex-col border-l bg-muted/20 max-h-[calc(90vh-140px)]">
            {/* Header do preview */}
            <div className="flex items-center gap-2 px-4 py-3 border-b bg-background/80 backdrop-blur-sm flex-shrink-0">
              <Eye className="w-3.5 h-3.5 text-muted-foreground" />
              <div>
                <h3 className="text-xs font-semibold">Preview</h3>
                <p className="text-[10px] text-muted-foreground">Visualização em tempo real</p>
              </div>
            </div>

            {/* Área de preview */}
            <div className="flex-1 overflow-y-auto">
              {!selectedInbox ? (
                // Estado vazio — nenhuma inbox selecionada
                <div className="flex flex-col items-center justify-center h-full p-6 text-center">
                  <div className="w-12 h-12 rounded-full bg-muted/50 flex items-center justify-center mb-3">
                    <MessageSquare className="w-5 h-5 text-muted-foreground/50" />
                  </div>
                  <p className="text-xs text-muted-foreground">
                    Selecione uma inbox para ver o preview da mensagem.
                  </p>
                </div>
              ) : (
                <ReminderMessagePreview
                  channelType={selectedInbox.channel_type}
                  inboxName={selectedInbox.name}
                  messageText={previewText}
                  messageType={derivedMessageType}
                  templateName={selectedWabaTemplate?.name}
                  variableMapping={variableMapping}
                />
              )}
            </div>
          </aside>
        </div>

        {/* ===== FOOTER ===== */}
        <div className="flex items-center justify-end gap-2 px-6 py-3 border-t bg-muted/10">
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={() => onOpenChange(false)}
            disabled={isSubmitting}
          >
            Cancelar
          </Button>
          <Button
            type="submit"
            size="sm"
            form="reminder-rule-form"
            disabled={isSubmitting}
            className="min-w-[100px] gap-1.5"
          >
            {isSubmitting && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
            {editingRule ? 'Salvar alterações' : 'Criar regra'}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
